import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import { Container } from 'src/components/layouts/Container/Container';
import HeadlineWithButtons from 'src/components/ui/HeadlineWithButtons/HeadlineWithButtons';
import TabBar, { TabProps } from 'src/components/layouts/TabBar/TabBar';
import { useTranslation } from 'react-i18next';
import IntegrationCard from 'src/components/cardLayouts/IntegrationCard/IntegrationCard';
import { useSearchParams } from 'react-router-dom';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Modal from 'src/components/layouts/Modal/Modal';
import { useConnectorMutations, useConnectorsQuery, useConnectorTemplatesQuery } from 'src/services/connector';
import Image from 'src/components/ui/Image/Image';
import API_BASE_URL from 'src/constants';
import { useRouteAccess } from 'src/hooks/useRouteAccess';
import styles from './styles/Integrations.module.scss';
import Overlay from '../components/ui/Overlay/Overlay';
import { useNavigationItems } from '../hooks/useNavigationItems';
import { IntegrationAddAndActivateForm } from '../components/layouts/IntegrationOverlay/IntegrationAddAndActivateForm';

enum TabKeys {
  AVAILABLE = 'all',
  ACTIVE = 'active',
  NON_ACTIVE = 'none_active',
}

const tabLabels: Record<TabKeys, string> = {
  [TabKeys.AVAILABLE]: 'integrations.available',
  [TabKeys.ACTIVE]: 'integrations.active',
  [TabKeys.NON_ACTIVE]: 'integrations.non_active',
};

function Integrations() {
  const { t } = useTranslation();
  const [visibleActivateTemplateModalId, setVisibleActivateModalId] = useState<number | null>(null);
  const [visibleDeactivateModalId, setVisibleDeactivateModalId] = useState<number | null>(null);
  const [showActivationSuccessModal, setShowActivationSuccessModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as TabKeys) || TabKeys.ACTIVE;
  const [activeTab, setActiveTab] = useState<TabKeys>(initialTab);
  const [showActivate, setShowActivate] = useState<boolean>(false);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<number | null>(null);

  const { data: connectorTemplates } = useConnectorTemplatesQuery();
  const { data: connectors } = useConnectorsQuery();
  const { data: disabledConnectors } = useConnectorsQuery({
    domain: `[('active','=',False)]`,
    enabled: activeTab === TabKeys.NON_ACTIVE,
  });

  const { deactivateMutation, updateMutation } = useConnectorMutations();

  const { findAccessRights } = useNavigationItems();
  const accessRights = findAccessRights('integrations');
  // The API-docs card is gated by the company-scoped `integrations/api-docs`
  // feature flag (df_feature_flag.feature_flag_integrations_api_docs).
  const { hasAccess } = useRouteAccess();
  const showApiDocsCard = hasAccess('integrations/api-docs');
  const addAndActivateFormRef = useRef<HTMLFormElement>(null);

  const handleTabChange = (tabKey: TabKeys) => {
    setActiveTab(tabKey);
    setSearchParams({ tab: tabKey }); // Update the search parameters
  };

  const tabs: TabProps[] = [
    {
      label: t(tabLabels[TabKeys.AVAILABLE]),
      key: TabKeys.AVAILABLE,
      onClick: () => handleTabChange(TabKeys.AVAILABLE),
    },
    {
      label: t(tabLabels[TabKeys.ACTIVE]),
      key: TabKeys.ACTIVE,
      onClick: () => handleTabChange(TabKeys.ACTIVE),
    },
    {
      label: t(tabLabels[TabKeys.NON_ACTIVE]),
      key: TabKeys.NON_ACTIVE,
      onClick: () => handleTabChange(TabKeys.NON_ACTIVE),
    },
  ];

  const activeIndex = tabs.findIndex(tab => tab.key === activeTab);

  const handleOnActivate = async (type: IntegrationCardData['type'], id: number) => {
    if (activeTab === TabKeys.NON_ACTIVE) {
      setVisibleActivateModalId(id);
    } else {
      setShowActivate(true);
      setSelectedIntegrationId(id);
    }
  };

  const handleOnDeactivate = (id: number) => {
    setVisibleDeactivateModalId(id);
  };

  const onConfirmDeactivate = async (connectorId: number) => {
    await deactivateMutation.mutateAsync({ params: { path: { connector_id: connectorId } } });

    setVisibleDeactivateModalId(null);
  };

  const onConfirmAddAndActivate = async (connectorId: number) => {
    await updateMutation.mutateAsync({
      params: {
        path: {
          connector_id: connectorId,
        },
      },
      body: {
        active: true,
      },
    });
    setVisibleActivateModalId(null);
  };

  const onCloseSuccessModal = () => {
    setShowActivationSuccessModal(false);
  };

  const handleCloseOverlay = () => {
    setShowActivate(false);
  };

  type IntegrationCardData = {
    id: number;
    image: string;
    title: string;
    host?: string | null;
    description: string;
    active: boolean;
    type: 'template' | 'connector';
    externalUrl?: string;
    buttonLabel?: string;
  };
  let integrationCardData: IntegrationCardData[] = [];

  if (activeTab === TabKeys.AVAILABLE) {
    integrationCardData =
      connectorTemplates?.map(connectorTemplate => ({
        id: connectorTemplate.id,
        image: connectorTemplate?.channel?.logo_url || '/assets/integrations/weship.png',
        version: '1.0.0',
        title: connectorTemplate.channel?.name || '',
        description: connectorTemplate?.channel?.description || '',
        active: false,
        type: 'template',
      })) ?? [];
  } else if (activeTab === TabKeys.ACTIVE) {
    integrationCardData =
      connectors?.items
        ?.filter(connector => connector.active)
        ?.map(connector => ({
          id: connector.id,
          image: connector.template?.channel?.logo_url || '/assets/integrations/weship.png',
          title: connector.name,
          host: connector.host,
          description: connector.template?.channel?.description || '',
          active: true,
          type: 'connector',
        })) ?? [];
  } else if (activeTab === TabKeys.NON_ACTIVE) {
    integrationCardData =
      disabledConnectors?.items
        ?.filter(connector => !connector.active)
        ?.map(connector => ({
          id: connector.id,
          image: connector?.template?.channel?.logo_url || '/assets/integrations/weship.png',
          title: connector?.name || connector?.template?.channel?.name || '',
          host: connector.host,
          description: connector.template?.channel?.description || '',
          active: false,
          type: 'connector',
        })) ?? [];
  }

  if (activeTab === TabKeys.AVAILABLE && showApiDocsCard) {
    // Static card linking to the external API documentation — not a connector.
    integrationCardData = [
      ...integrationCardData,
      {
        id: -1,
        image: '',
        title: t('integrations.api_docs_title'),
        description: t('integrations.api_docs_description'),
        active: false,
        type: 'template',
        externalUrl: `${API_BASE_URL}api/docs`,
        buttonLabel: t('integrations.open_api_docs'),
      },
    ];
  }
  return (
    <Container title={`${t('integrations.integrations')}`} className={classNames(styles.root, 'relative py-32 flex-1')}>
      <HeadlineWithButtons headline={t('integrations.all_integrations')} />
      <div className="mb-24">
        <TabBar defaultIndex={activeIndex} tabs={tabs} />
        {activeTab === TabKeys.AVAILABLE && <div />}
        {activeTab === TabKeys.ACTIVE && <div />}
        {activeTab === TabKeys.NON_ACTIVE && <div />}
      </div>
      {integrationCardData?.length === 0 ? (
        <div
          className="flex flex-col justify-center items-center gap-16 rounded-sm w-full px-32 py-80 border-dashed border-2"
          style={{ borderColor: 'var(--border-distinct)' }}>
          <Image
            imageUrl="/placeholder.png"
            alt=""
            fallbackComponent={<div className="surface-skeleton-faded w-full h-full rounded-sm" />}
            className="w-48 h-48"
          />
          <p className="typo-title-20 md:typo-title-24 text-disabled text-center">
            {activeTab === TabKeys.AVAILABLE && t('integrations.no_results_found_available')}
            {activeTab === TabKeys.ACTIVE && t('integrations.no_results_found_active')}
            {activeTab === TabKeys.NON_ACTIVE && t('integrations.no_results_found_non_active')}
          </p>
        </div>
      ) : (
        <div className={styles.integrationsGrid}>
          {integrationCardData?.map(connector => (
            <IntegrationCard
              key={`${connector.id}-${connector.type}`}
              {...connector}
              titleUrl={connector.active ? `/integrations/connectors/${connector.id}` : undefined}
              onActivate={() => handleOnActivate(connector.type, connector.id)}
              onDeactivate={() => handleOnDeactivate(connector.id)}
              allowEdition={accessRights?.read_write}
            />
          ))}
        </div>
      )}
      <Modal isOpen={visibleDeactivateModalId !== null} onClose={() => setVisibleDeactivateModalId(null)}>
        <div className="max-w-460">
          <div className="border-subtle-bottom p-32">
            <h4 className="mb-12 typo-title-24">{t('integrations.deactivate_api')}</h4>
            <p className="typo-text-16 text-faded">{t('integrations.deactivate_api_text')}</p>
          </div>
          <div className="border-subtle-bottom py-16 px-24 flex justify-end">
            <Button
              className="mr-12"
              onClick={() => setVisibleDeactivateModalId(null)}
              tone={ButtonToneProps.SECONDARY}
              title={t('general.cancel')}
            />
            <Button
              onClick={() => {
                if (visibleDeactivateModalId !== null) {
                  onConfirmDeactivate(visibleDeactivateModalId);
                }
              }}
              tone={ButtonToneProps.PRIMARY_BLUE}
              title={t('general.confirm')}
            />
          </div>
        </div>
      </Modal>
      <Modal isOpen={visibleActivateTemplateModalId !== null} onClose={() => setVisibleActivateModalId(null)}>
        <div className="max-w-460">
          <div className="border-subtle-bottom p-32">
            <h4 className="mb-12 typo-title-24">{t('integrations.add_activate_api')}</h4>
            <p className="typo-text-16 text-faded">{t('integrations.add_activate_api_text')}</p>
          </div>
          <div className="border-subtle-bottom py-16 px-24 flex justify-end">
            <Button
              className="mr-12"
              onClick={() => setVisibleActivateModalId(null)}
              tone={ButtonToneProps.SECONDARY}
              title={t('general.cancel')}
            />
            <Button
              onClick={() => {
                if (visibleActivateTemplateModalId !== null) {
                  onConfirmAddAndActivate(visibleActivateTemplateModalId);
                }
              }}
              tone={ButtonToneProps.PRIMARY_BLUE}
              title={t('general.confirm')}
            />
          </div>
        </div>
      </Modal>
      <Modal isOpen={showActivationSuccessModal} onClose={onCloseSuccessModal}>
        <div className="max-w-460 flex flex-col justify-center items-center p-32 pb-40">
          <img src="/assets/integrations/illustration.png" alt="success" className="w-80 h-80" />
          <h4 className="mb-12 typo-title-24 text-center">{t('integrations.integration_successful_title')}</h4>
          <p className="typo-text-16 text-faded text-center">{t('integrations.integration_successful_text')}</p>
        </div>
      </Modal>
      {showActivate && (
        <Overlay>
          <IntegrationAddAndActivateForm
            ref={addAndActivateFormRef}
            connectorTemplateId={selectedIntegrationId as number}
            allowEdition={accessRights?.read_write}
            onClose={handleCloseOverlay}
          />
        </Overlay>
      )}
    </Container>
  );
}

export default Integrations;
